import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { SpriteIcon } from '@/components/ui/SvgSprite';
import { Message } from 'iconsax-react';
import { BlueCheck, GoldCheck, RankedBadge } from '@/components/Icons';
import { LayoutProps } from '@/types/generalSettings';
import { isBrokerExchangeOrProp } from '@/utils/layout/typeGuards';
import { getBadge } from '@/components/(postTypes)/platforms/(archive)/layout/ExchangesListCard';
import { toPersianDigits } from '@/utils/persianUtils';

interface ExchangeChildItemsProps {
    childItem?: LayoutProps.MenuItem;
    index: number;
    setOpen?: (state: boolean) => void;
    menu?: boolean;
    className?: string;
    isAd?: undefined | boolean;
    hasAd?: boolean;
}

const ExchangeMenuItemSimple: React.FC<ExchangeChildItemsProps> = ({
    childItem,
    index,
    setOpen,
    menu,
    className,
    isAd,
    hasAd,
}) => {
    const connectedObject = childItem?.connectedObject;

    if (!isBrokerExchangeOrProp(connectedObject)) {
        return null;
    }

    if (
        connectedObject?.contentTypeName === 'exchange' ||
        connectedObject?.contentTypeName === 'broker' ||
        connectedObject?.contentTypeName === 'prop' ||
        connectedObject?.contentTypeName === 'gold-platforms'
    ) {
        const { faName, enName, logo, badge, sponsored, verified } =
            connectedObject?.brokerSingle?.newfields || {};

        const rating =
            connectedObject?.contentTypeName === 'broker'
                ? connectedObject?.brokerFields?.generalInfo?.brokerRating?.finalRating
                : connectedObject?.contentTypeName === 'exchange'
                  ? connectedObject?.exchangeFields?.generalInfo?.brokerRating?.finalRating
                  : connectedObject?.contentTypeName === 'prop'
                    ? connectedObject?.exchangeFields?.generalInfo?.brokerRating?.finalRating
                    : connectedObject?.contentTypeName === 'gold-platforms'
                      ? connectedObject?.goldFields?.generalInfo?.brokerRating?.finalRating
                      : 0;

        // --- BADGE LOGIC ---
        const adjustedIndex = hasAd ? index - 1 : index;
        let badgeDisplay;

        if (isAd) {
            // Case 1: Advertisement
            badgeDisplay = <RankedBadge className={'size-9'} />;
        } else if (adjustedIndex < 3) {
            // Case 2: Top 3 Ranks (1, 2, 3) -> Show SVG Badge
            badgeDisplay = getBadge(adjustedIndex, false, menu ? 'size-7' : '');
        } else {
            // Case 3: Rank 4+ -> Show Persian Number
            // adjustedIndex is 0-based, so Rank 4 is index 3. We display index + 1.
            badgeDisplay = (
                <span className='text-base font-bold text-gray-500 dark:text-white/50'>
                    {toPersianDigits(adjustedIndex + 1)}
                </span>
            );
        }

        return (
            <Link
                onClick={() => (setOpen ? setOpen(false) : true)}
                href={childItem?.path || '#'}
                prefetch={false}
                className={`relative flex overflow-hidden py-2 transition-all lg:hover:bg-primary/5 lg:dark:hover:bg-white/5 ${className}`}
            >
                {/* 1. BADGE COLUMN */}
                <div className={`w-16 flex justify-center items-center`}>{badgeDisplay}</div>

                <div
                    className={`w-full border-r border-primary/10 dark:border-white/20 pr-3 lg:pr-1 flex flex-col lg:flex-row justify-center lg:justify-between text-white lg:items-center gap-3`}
                >
                    <div className={'flex items-center gap-2'}>
                        {logo?.mediaItemUrl ? (
                            <Image
                                className={'size-8 rounded-none object-contain'}
                                src={logo.mediaItemUrl}
                                alt={enName || 'Logo'}
                                width={120}
                                height={120}
                                loading={'lazy'}
                            />
                        ) : (
                            <div className='size-8 rounded-none bg-gray-100 flex items-center justify-center text-xs text-gray-400'>
                                Logo
                            </div>
                        )}
                        <div>
                            <div
                                className={'flex flex-col items-start dark:text-white text-primary'}
                            >
                                <div
                                    className={`inline-flex items-center gap-1 ${menu && 'text-xs'}`}
                                >
                                    <span className={'line-clamp-1'}>{faName}</span>
                                    <span className={`text-xxs dark:text-white/80 text-primary/80`}>
                                        ({enName})
                                    </span>
                                    <div className={'w-4'}>
                                        {sponsored ? (
                                            <GoldCheck className={'w-full h-full'} />
                                        ) : verified ? (
                                            <BlueCheck className={'w-full h-full'} />
                                        ) : (
                                            <></>
                                        )}
                                    </div>
                                </div>
                                {badge && !menu && (
                                    <span className={'text-xs text-progress font-medium lg:hidden'}>
                                        {toPersianDigits(badge)}
                                    </span>
                                )}
                            </div>

                            <div
                                className={`flex gap-3 justify-start group-hover:text-white mt-2 ${menu && '!gap-1'}`}
                            >
                                {badge && !menu && (
                                    <>
                                        <span
                                            className={
                                                'text-xs text-progress font-medium max-lg:hidden'
                                            }
                                        >
                                            {toPersianDigits(badge)}
                                        </span>
                                        <span
                                            className={`text-xs font-medium text-primary/40 dark:text-white/40 max-lg:hidden`}
                                        >
                                            &#9679;
                                        </span>
                                    </>
                                )}

                                <div
                                    className={
                                        'inline-flex items-center gap-2 text-xs text-primary/60 dark:text-white/40'
                                    }
                                >
                                    <Message
                                        size='16'
                                        className={
                                            'leading-none stroke-primary/60 dark:stroke-white/60'
                                        }
                                    />
                                    <div className='flex gap-1'>
                                        {/* 2. COMMENT COUNT */}
                                        <span>
                                            {toPersianDigits(connectedObject?.postRatings?.count)}
                                        </span>
                                        <span>نظر</span>
                                    </div>
                                </div>
                                <span
                                    className={
                                        'text-xs font-medium text-primary/40 dark:text-white/40'
                                    }
                                >
                                    &#9679;
                                </span>
                                <div
                                    className={
                                        'flex items-center gap-x-1 text-primary/60 dark:text-white/40'
                                    }
                                >
                                    <span>
                                        <SpriteIcon
                                            name='star'
                                            className={`text-gold/75 dark:text-gold/30 fill-gold/80 dark:fill-gold/60`}
                                            strokeWidth={1.5}
                                            size={12}
                                        />
                                    </span>
                                    <span className={'text-xs flex gap-1'}>
                                        {/* 3. RATINGS */}
                                        <span>{toPersianDigits(rating)}</span>
                                        <span>از</span>
                                        <span>{toPersianDigits(5)}</span>
                                        <span>امتیاز</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Link>
        );
    }
};

export default ExchangeMenuItemSimple;
